/**
 * Fire timeline for Screen 4 — one row per campaign step in seq order.
 * Failed steps expose inline retry/skip buttons (Screen 5). Reuses the
 * existing `useRetryCampaignStep` mutation from the manual campaign flow.
 */
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Circle,
  CircleX,
  Clock,
  ExternalLink,
  MinusCircle,
  RotateCcw,
  Zap,
} from 'lucide-react';
import type { CampaignStep } from '@seo/types';
import { Button } from '@/components/ui/button';
import { Section, SectionHeader, SectionTitle } from '@/components/ui/section';
import { Spinner } from '@/components/spinner';
import { StatusPill } from '@/components/status-pill';
import { cn } from '@/lib/cn';
import { summarizeApiError } from '@/lib/api';
import { useRetryCampaignStep } from '@/features/campaigns/use-retry-campaign-step';
import { detailUrlFor } from './outputs-tree';

const STATUS_ICON: Record<string, JSX.Element> = {
  pending: <Circle className="h-4 w-4 text-muted-foreground" />,
  queued: <Clock className="h-4 w-4 text-amber-500" />,
  running: <Zap className="h-4 w-4 text-primary" />,
  completed: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
  failed: <CircleX className="h-4 w-4 text-destructive" />,
  skipped: <MinusCircle className="h-4 w-4 text-muted-foreground" />,
  cancelled: <CircleX className="h-4 w-4 text-muted-foreground" />,
};

export interface FireTimelineProps {
  campaignId: string;
  steps: CampaignStep[] | undefined;
  isLoading: boolean;
}

export function FireTimeline({
  campaignId,
  steps,
  isLoading,
}: FireTimelineProps): JSX.Element {
  const retryStep = useRetryCampaignStep(campaignId);

  if (isLoading && !steps) {
    return (
      <div className="flex justify-center p-6">
        <Spinner />
      </div>
    );
  }

  const rows = steps ?? [];

  return (
    <Section>
      <SectionHeader>
        <SectionTitle className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          Timeline
          {rows.length > 0 ? (
            <span className="text-xs font-normal text-muted-foreground">
              ({rows.length} step{rows.length === 1 ? '' : 's'})
            </span>
          ) : null}
        </SectionTitle>
      </SectionHeader>

      <ol className="space-y-1.5">
        {rows.map((s) => {
          const busy = retryStep.isPending && retryStep.variables === s.id;
          const detailUrl = detailUrlFor(s);
          return (
            <li key={s.id}>
              <div
                className={cn(
                  'flex items-start gap-3 rounded-md border border-border/60 bg-card/40 p-3',
                  s.status === 'running' && 'border-primary/40 bg-primary/5',
                  s.status === 'failed' && 'border-destructive/40 bg-destructive/5',
                )}
              >
                <div className="mt-0.5">
                  {STATUS_ICON[s.status] ?? STATUS_ICON.pending}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-muted-foreground">
                      #{s.seq}
                    </span>
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">
                      {s.step_type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="mt-1 text-sm font-medium text-foreground">
                    {s.title}
                  </div>
                  {s.rationale ? (
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {s.rationale}
                    </div>
                  ) : null}
                  {s.failure_reason ? (
                    <div className="mt-1 text-xs text-destructive">
                      Error: {s.failure_reason}
                    </div>
                  ) : null}
                  {(s.started_at || s.completed_at) ? (
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {s.started_at
                        ? new Date(s.started_at).toLocaleTimeString()
                        : ''}
                      {s.completed_at
                        ? ` → ${new Date(s.completed_at).toLocaleTimeString()}`
                        : ''}
                    </div>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {detailUrl ? (
                    <Button asChild size="sm" variant="ghost">
                      <Link to={detailUrl.to} params={detailUrl.params}>
                        <ExternalLink className="h-3 w-3" />
                        View
                      </Link>
                    </Button>
                  ) : null}
                  <StatusPill status={s.status} />
                  {s.status === 'failed' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() =>
                        retryStep
                          .mutateAsync(s.id)
                          .then(() => toast.success('Retry started'))
                          .catch((err) => {
                            const summary = summarizeApiError(err, {
                              fallbackTitle: 'Retry failed',
                            });
                            toast.error(summary.title, {
                              description: summary.description,
                            });
                          })
                      }
                    >
                      {busy ? (
                        <Spinner className="h-3.5 w-3.5" />
                      ) : (
                        <RotateCcw className="h-3.5 w-3.5" />
                      )}
                      Retry
                    </Button>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </Section>
  );
}
